import PublishIcon from "@mui/icons-material/Publish";
import { ArrowBack } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  IconButton,
  LinearProgress,
  Typography
} from "@mui/material";
import { usePostHog } from "@posthog/react";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { capture } from "../analytics/posthog";
import ContentScreenshotThumbnail from "../components/ContentScreenshotThumbnail";
import LessonPlanRenderer from "../components/lesson-plan/LessonPlanRenderer";
import ApiClient from "../config/ApiClient";
import { LessonPlanEditorProvider, useLessonPlanEditor } from "../context/LessonPlanEditorContext";
import { LessonPlanModel } from "../object-actions/types/types";

const LessonPlanViewContent = () => {
  const { enqueueSnackbar } = useSnackbar();
  const posthog = usePostHog();
  const { isEditMode, loading: saveLoading, hasChanges, saveAll, resetChanges, toggleEditMode, lessonPlan, setLessonPlan } = useLessonPlanEditor();
  const [isPublishing, setIsPublishing] = useState(false);
  const { id } = useParams();

  const isCoach = lessonPlan?.my_roles?.includes('coach') ?? false;
  const canViewCoachContent = isCoach || (lessonPlan?.my_roles?.includes('parent') ?? false);
  const isPublished = !!lessonPlan?.coach_delivered;

  const handleSave = async () => {
    capture(posthog, "lesson_plan_save_clicked", { lesson_plan_id: lessonPlan?.id });
    await saveAll();
    // Optionally remove hash after save
    if (window.location.hash === '#editing=true') {
      window.location.hash = '';
    }
  };

  const handleCancel = () => {
    capture(posthog, "lesson_plan_cancel_clicked", { lesson_plan_id: lessonPlan?.id });
    resetChanges();
    window.location.hash = '';
  };

  const handlePublish = async () => {
    if (!lessonPlan?.id || isPublishing || !id) {
      return;
    }
    capture(posthog, "lesson_plan_publish_clicked", { lesson_plan_id: lessonPlan.id });
    setIsPublishing(true);
    try {
      const response = await ApiClient.post(`/api/lesson-plan/${lessonPlan.id}/publish/`, {});

      if (response.success) {
        capture(posthog, "lesson_plan_published", { lesson_plan_id: lessonPlan.id });
        // Refetch the lesson plan to get updated data with sessions
        const refetchResponse = await ApiClient.get<LessonPlanModel>(
          `/api/lesson-plan/${id}?include_sessions=true`
        );

        if (refetchResponse.success && refetchResponse.data) {
          setLessonPlan(refetchResponse.data);
          enqueueSnackbar('Lesson plan published successfully!', { variant: 'success' });
        } else {
          enqueueSnackbar('Lesson plan published, but failed to refresh data', { variant: 'warning' });
        }
      } else {
        enqueueSnackbar('Failed to publish lesson plan', { variant: 'error' });
      }
    } catch (error) {
      console.error('Error publishing lesson plan:', error);
      enqueueSnackbar('Error publishing lesson plan', { variant: 'error' });
    } finally {
      setIsPublishing(false);
    }
  };

  if (!lessonPlan) {
    return <LinearProgress />;
  }

  return (
    <Container maxWidth="lg" sx={{ py: 2 }}>
      <Box sx={{ mb: 2 }}>
        {/* Back to progress report (coach and parent when athlete context exists) */}
        {canViewCoachContent && lessonPlan.assignment?.id && (
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              component={Link}
              to={`/program-progress/${lessonPlan.assignment.id}`}
              color="secondary"
              aria-label="Back to progress report"
            >
              <ArrowBack />
            </IconButton>
            <Typography component={Link} to={`/program-progress/${lessonPlan.assignment.id}`} variant="body2" color="text.secondary" sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
              Back to progress report
            </Typography>
          </Box>
        )}
        {/* Header: title and screenshot for coach + parent; edit/publish actions for coach only */}
        {canViewCoachContent && (
          <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <ContentScreenshotThumbnail
                screenshotLight={lessonPlan.screenshot_light}
                screenshotDark={lessonPlan.screenshot_dark}
                alt="Lesson plan preview"
                data-attr="lesson-plan-view_screenshot-thumbnail"
              />
              <Box>
                <Typography
                  variant="h3"
                  component="h1"
                  gutterBottom
                  sx={{
                    fontWeight: 'bold',
                    color: 'primary.main',
                    mb: 2
                  }}
                >
                  Lesson Plan
                </Typography>
              </Box>
            </Box>

            {/* Action Buttons (coach only) */}
            {isCoach && (
              <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                {!isEditMode ? (
                  <>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        capture(posthog, "lesson_plan_edit_toggle_clicked", { lesson_plan_id: lessonPlan?.id });
                        toggleEditMode();
                      }}
                      data-attr="lesson-plan-view_edit"
                    >
                      Edit
                    </Button>
                    {!isPublished && (
                      <Button
                        variant="contained"
                        color="success"
                        onClick={handlePublish}
                        disabled={isPublishing}
                        startIcon={isPublishing ? <CircularProgress size={20} /> : <PublishIcon />}
                        data-attr="lesson-plan-view_publish"
                      >
                        {isPublishing ? 'Publishing...' : 'Publish'}
                      </Button>
                    )}
                  </>
                ) : (
                  <>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleSave}
                      disabled={saveLoading || !hasChanges}
                      startIcon={saveLoading ? <CircularProgress size={20} /> : undefined}
                      data-attr="lesson-plan-view_save"
                    >
                      Save
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={handleCancel}
                      disabled={saveLoading}
                      data-attr="lesson-plan-view_cancel"
                    >
                      Cancel
                    </Button>
                  </>
                )}
              </Box>
            )}
          </Box>
        )}

        <Box>
          <LessonPlanRenderer entity={lessonPlan as any} />
        </Box>

      </Box>
    </Container>
  );
};

const LessonPlanView = () => {
  const { id } = useParams();
  const [initialLessonPlan, setInitialLessonPlan] = useState<LessonPlanModel | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {

    const fetchLessonPlan = async () => {
      if (!id) {
        setError("Lesson plan ID is required");
        setLoading(false);
        return;
      }

      try {
        // Request with include_sessions to get all nested data
        const response = await ApiClient.get<LessonPlanModel>(
          `/api/lesson-plan/${id}?include_sessions=true`
        );

        if (response.error) {
          setError(response.error);
        } else if (response.success && response.data) {
          setInitialLessonPlan(response.data);
        } else {
          setError("Failed to load lesson plan");
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Unknown error occurred");
      } finally {
        setLoading(false);
      }
    };

    fetchLessonPlan();
  }, [id]);

  if (loading) {
    return <LinearProgress />;
  }

  if (error) {
    return <div>{error}</div>;
  }

  return (
    <div id="LessonPlanView" data-testid="lesson-plan-view">
      <LessonPlanEditorProvider initialLessonPlan={initialLessonPlan}>
        <LessonPlanViewContent />
      </LessonPlanEditorProvider>
    </div>
  );
};

export default LessonPlanView;
